import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import { fakeTableList, schema, stepper } from "./parts";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../context/globalContext";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { BusinessContext } from "../../context/businessContext";
import TkStepper from "../../components/atoms/TkStepper";
import { httpAPI_BUS006 } from "./api/request";
import { CheckedList } from "../../type/apiType/IAPI_BUS006";
import _ from "lodash";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";

/**
 * SCRC060104 配置換え　内容入力
 * @returns SCRC060104 配置換え　内容入力
 */
const SCRC060104 = () => {
  const navigate = useNavigate();
  // 認証情報
  const { state, setState } = useContext(GlobalContext);
  // 持回情報
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const [stateInfoTable, setStateInfoTable] = useState<any[]>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [staffNos, setStaffNos] = useState<any[]>([]);
  const facilityList = _.cloneDeep(
    businessState.job_27?.removeFacilityList ?? []
  );
  const [openWarning, setOpenWarning] = useState(false);
  const reqDataRef = useRef<any>();

  // ユーザタイプ
  const userType = state.authInfo?.userType;
  // ユーザタイプが機構か
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  // チェック済みリスト
  const [checkedList, setCheckedList] = useState<CheckedList[]>([]);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    if (isUserTypeKiko) {
      let contractorName = resArr.find(
        (item) => item.keyword === "contractorName"
      )!;
      contractorName.text = `${businessState.common?.contractorNo}:${businessState.common?.contractorName}`;
      contractorName.showLine = true;
    }
    // 配置換え職員氏名
    let staffName = resArr.find((item) => item.keyword === "staffName")!;
    const staffList = [
      ...(_.cloneDeep(businessState.job_27?.removeStaffList) ?? []),
    ];
    if (staffList && staffList.length > 0) {
      staffName.node = (
        <Box>
          {staffList.map((staff: any, index: number) => {
            return staff.checkFlag === "1" ? (
              <Typography key={index}>
                {staff?.staffNo}:{staff?.staffName}
              </Typography>
            ) : null;
          })}
        </Box>
      );
      setStaffNos(
        staffList
          .filter((i) => i.checkFlag === "1")
          .map((staff: any) => {
            return { staffNo: staff.staffNo };
          })
      );
    }

    // 配置換え元の施設名
    let is999 = businessState.job_27?.facilityNo === "999";
    let bFacilityName = resArr.find(
      (item) => item.keyword === "bFacilityName"
    )!;
    if (businessState.job_27?.facilityNo) {
      const name =
        facilityList.find(
          (facility: any) =>
            facility?.facilityNo === businessState.job_27?.facilityNo
        )?.facilityName || businessState.job_27?.facilityName;
      bFacilityName.text = `${businessState.job_27?.facilityNo}:${name}`;
    }

    // 配置換え先の施設名
    let aFacilityName = resArr.find(
      (item) => item.keyword === "aFacilityName"
    )!;
    const facilitySelectList: any = [];

    facilityList.forEach((facility: any) => {
      if (facility?.facilityNo !== businessState.job_27?.facilityNo) {
        facilitySelectList.push({
          label: `${facility?.facilityNo}:${facility?.facilityName}`,
          value: `${facility?.facilityNo}:${facility?.facilityName}`,
        });
      }
    });

    if (!is999) {
      facilitySelectList.push({
        label: "999:契約対象外施設",
        value: "999:契約対象外施設",
      });
    }
    aFacilityName.selectPropsList = _.cloneDeep(facilitySelectList);
    setStateInfoTable([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //登録ボタン押下時
  const onClickSubmit = (data: any) => {
    reqDataRef.current = data;
    //業務チェック
    const today = new Date();
    const nextYearDate = new Date(
      today.setFullYear(today.getFullYear() + 1)
    ).setHours(0, 0, 0, 0);
    const commutationDate = new Date(data.commutationDate).setHours(0, 0, 0, 0);
    if (commutationDate >= nextYearDate) {
      //入出力項目.配置換え先施設の配属日 ≧ システム日付 + 1年
      setOpenWarning(true);
      return;
    } else {
      onCheckAndSubmit();
    }
  };

  // 登録
  const onCheckAndSubmit = async () => {
    //配置換えチェックAPIを呼び出す
    try {
      const res = await httpAPI_BUS006({
        checkedFlg: "1", // チェック済みフラグ
        contractorNo: isUserTypeKiko
          ? businessState.common?.contractorNo!
          : state.authInfo?.contractorNo!, // 共済契約者番号
        aFacilityNo: reqDataRef.current.aFacilityName.split(":")[0], // 配置換え後施設番号
        reassignmentDate: reqDataRef.current.commutationDate, // 配置換日
        staffList: staffNos,
        checkedList: checkedList,
      });

      setCheckedList(res.checkedList || []);

      if (res.resultCd === "I0000") {
        setCheckedList([]);
        let removeStaffList = businessState.job_27?.removeStaffList ?? [];
        if (removeStaffList.length > 0) {
          removeStaffList = removeStaffList.map((staff) => {
            if (staff.checkFlag === "1") {
              staff.removeFacilityNo =
                reqDataRef.current.aFacilityName.split(":")[0];
              staff.removeFacilityName =
                reqDataRef.current.aFacilityName.split(":")[1];
              staff.removeDate = reqDataRef.current.commutationDate;
            }
            return staff;
          });
        }
        setBusinessState({
          ...businessState,
          job_27: {
            ...businessState.job_27,
            removeStaffList,
          },
        });

        navigate("/SCRC060103");
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
          errorMsgValues: res.errorStaffNo ?? res.error229StaffNo,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const onClickWarningOk = () => {
    setOpenWarning(false);
    onCheckAndSubmit();
  };

  return (
    <Box className="SCRC060104" data-testid="SCRC060104PageForTest">
      <TkStepper steps={stepper} activeStep={0} savaStep={0} />
      <TkInfoManual rule={"\\n"}>{labelids.labelid.LBLC060104_01}</TkInfoManual>
      <TkFakeTable
        list={stateInfoTable}
        ruleSchema={schema}
        buttonGroup={<TkBackButton />}
        submitButtonText="登録"
        onCheckAndSubmit={(data: any) => onClickSubmit(data)}
      />
      <TkMessageDialog
        open={openWarning}
        onOky={onClickWarningOk}
        onCancel={() => setOpenWarning(false)}
        onClose={() => setOpenWarning(false)}
        title="警告"
        contextText={TkPropertyMessageGet("TKMSG_SCR0026W")}
        okText="確認"
        cancelText="戻る"
      />
    </Box>
  );
};

export default SCRC060104;
